import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, EnvelopeSimple, Clock, CircleNotch } from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { PageHero } from "../components/PageHero";
import { api, formatApiErrorDetail, useSettings } from "../lib/api";

export default function ContactPage() {
  const { data: s } = useSettings();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/forms/contact", form);
      toast.success("Message sent. We'll respond within one business day.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err: any) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const info = [
    { icon: MapPin, label: "Headquarters", value: s?.address || "13401 W. Eight Mile Rd, Detroit, MI 48235" },
    { icon: Phone, label: "Phone", value: s?.phone || "313-559-5058" },
    { icon: EnvelopeSimple, label: "Email", value: s?.email || "foster.terrya@fosche.us" },
    { icon: Clock, label: "Hours", value: s?.hours || "Mon–Fri: 8:00 AM – 6:00 PM" },
  ];

  return (
    <div data-testid="contact-page">
      <PageHero
        overline="Contact"
        title="Let's talk about your project."
        subtitle="Reach the FOSCHE team directly — for quotes, contracting inquiries, partnerships, or anything else."
      />

      <section className="bg-canvas py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14">
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 border border-line">
              {info.map((item, i) => (
                <div key={item.label} className={`p-6 border-line ${i % 2 === 1 ? "sm:border-l" : ""} ${i >= 2 ? "border-t" : ""}`}>
                  <item.icon size={22} className="text-blueprint mb-3" />
                  <p className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-sub">{item.label}</p>
                  <p className="mt-1.5 text-sm text-ink leading-snug break-words">{item.value}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 border border-line overflow-hidden">
              <iframe
                title="FOSCHE HQ Map"
                data-testid="contact-map"
                src="https://www.google.com/maps?q=13401+W+Eight+Mile+Rd,+Detroit,+MI+48235&output=embed"
                className="w-full h-72 md:h-80 grayscale contrast-110"
                loading="lazy"
              />
            </div>
          </div>

          <form onSubmit={submit} className="border border-line bg-panel p-7 md:p-9 h-fit" data-testid="contact-form">
            <p className="overline-label mb-6">Send a Message</p>
            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-1.5">
                  <Label htmlFor="c-name">Full Name *</Label>
                  <Input id="c-name" data-testid="contact-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none bg-canvas" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="c-email">Email *</Label>
                  <Input id="c-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none bg-canvas" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-1.5">
                  <Label htmlFor="c-phone">Phone</Label>
                  <Input id="c-phone" data-testid="contact-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-none bg-canvas" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="c-subject">Subject</Label>
                  <Input id="c-subject" data-testid="contact-subject-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="rounded-none bg-canvas" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="c-message">Message *</Label>
                <Textarea id="c-message" data-testid="contact-message-input" required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-none bg-canvas" />
              </div>
              <Button type="submit" data-testid="contact-submit-button" disabled={submitting} className="w-full rounded-none bg-[var(--blueprint)] hover:bg-[var(--gold)] hover:text-black">
                {submitting ? (
                  <span className="inline-flex items-center gap-2">
                    <CircleNotch size={16} className="animate-spin" /> Sending…
                  </span>
                ) : (
                  "Send Message"
                )}
              </Button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
